import { useCallback, useReducer } from "react";
import {
  any,
  append,
  concat,
  filter,
  includes,
  isEmpty,
  pluck,
  propEq,
  reject,
  uniq,
  without,
} from "ramda";

const actionTypes = {
  update_sort: "update_sort",
  update_page: "update_page",
  update_selected: "update_selected",
  update_edit: "update_edit",
  update_rows_per_page: "update_rows_per_page",
};

const useMediaTable = (callbacks = {}, initialState = {}) => {
  const mediaTableReducer = (state, action) => {
    switch (action.type) {
      case actionTypes.update_sort: {
        return {
          ...initialState,
          sort: { property: action.property, order: action.order },
        };
      }
      case actionTypes.update_page: {
        return { ...initialState, page: action.page };
      }
      case actionTypes.update_selected: {
        return { ...initialState, selected: action.selected };
      }
      case actionTypes.update_rows_per_page: {
        return { ...initialState, rowsPerPage: action.rowsPerPage };
      }
      default: {
        throw new Error(`Unhandled type in mediaTableReducer: ${action.type}`);
      }
    }
  };
  const [state, dispatch] = useReducer(mediaTableReducer, initialState);

  const handleRequestSort = useCallback(
    property => {
      const prop =
        property === state.sort.property && state.sort.order === "desc"
          ? null
          : property;

      const direction =
        state.sort.property !== prop
          ? "asc"
          : state.sort.order === "desc"
          ? "asc"
          : "desc";

      dispatch({
        type: actionTypes.update_sort,
        property: prop,
        order: direction,
      });
      callbacks.onSortChange &&
        callbacks.onSortChange({
          property: prop,
          order: direction,
        });
    },
    [state, callbacks],
  );

  const handleChangePage = useCallback(
    page => {
      dispatch({
        type: actionTypes.update_page,
        page,
      });
      callbacks.onPageChange && callbacks.onPageChange(page);
    },
    [callbacks],
  );

  const handleRowClick = useCallback(
    media => {
      if (media.status !== "TAKEDOWN") {
        const id = media.id;
        const updated = includes(id, state.selected)
          ? without([id], state.selected)
          : append(id, state.selected);

        dispatch({
          type: actionTypes.update_selected,
          selected: updated,
        });
        callbacks.onSelectChange && callbacks.onSelectChange(updated);
      }
    },
    [state, callbacks],
  );

  const handleSelectAllClick = useCallback(
    (target, itemList) => {
      if (target.checked && isEmpty(state.selected)) {
        const updated = pluck(
          "id",
          filter(item => item.status !== "TAKEDOWN", itemList),
        );

        dispatch({
          type: actionTypes.update_selected,
          selected: updated,
        });
        callbacks.onSelectChange && callbacks.onSelectChange(updated);
      } else {
        dispatch({
          type: actionTypes.update_selected,
          selected: [],
        });
        callbacks.onSelectChange && callbacks.onSelectChange([]);
      }
    },
    [state.selected, callbacks],
  );

  const handleSelectAllContent = useCallback(
    (target, itemList) => {
      const selectedContent = pluck(
        "id",
        filter(item => item.status !== "TAKEDOWN", itemList),
      );

      const updated = isEmpty(state.selected)
        ? selectedContent
        : target.checked
        ? without(selectedContent, state.selected)
        : uniq(concat(state.selected, selectedContent));

      dispatch({
        type: actionTypes.update_selected,
        selected: updated,
      });
      callbacks.onSelectChange && callbacks.onSelectChange(updated);
    },
    [state.selected, callbacks],
  );

  const handleCollectionRowClick = useCallback(
    (subcollectionId, parentAnthologyId) => {
      const updated = any(propEq("id", subcollectionId), state.selected)
        ? reject(propEq("id", subcollectionId), state.selected)
        : append(
            { id: subcollectionId, parentAnthologyId: parentAnthologyId },
            state.selected,
          );
      dispatch({
        type: actionTypes.update_selected,
        selected: updated,
      });
      callbacks.onSelectChange && callbacks.onSelectChange(updated);
    },
    [state, callbacks],
  );

  const handleCollectionSelectAllClick = useCallback(
    (target, itemList) => {
      if (target.checked) {
        const updated = itemList.map(subcollection => {
          return {
            id: subcollection.id,
            parentAnthologyId: subcollection.anthology
              ? subcollection.anthology.id
              : "",
          };
        });
        dispatch({
          type: actionTypes.update_selected,
          selected: updated,
        });
        callbacks.onSelectChange && callbacks.onSelectChange(updated);
      } else {
        dispatch({
          type: actionTypes.update_selected,
          selected: [],
        });
        callbacks.onSelectChange && callbacks.onSelectChange([]);
      }
    },
    [callbacks],
  );

  const handleRemoveAllSelected = useCallback(() => {
    dispatch({
      type: actionTypes.update_selected,
      selected: [],
    });
    callbacks.onSelectChange && callbacks.onSelectChange([]);
  }, [callbacks]);

  const handleUpdateRowsPerPage = useCallback(
    rowsPerPage => {
      dispatch({
        type: actionTypes.update_rows_per_page,
        rowsPerPage,
      });
      callbacks.onRowsPerPageChange &&
        callbacks.onRowsPerPageChange(rowsPerPage);
    },
    [callbacks],
  );

  return {
    state,
    handleRequestSort,
    handleChangePage,
    handleRowClick,
    handleSelectAllClick,
    handleSelectAllContent,
    handleCollectionRowClick,
    handleCollectionSelectAllClick,
    handleRemoveAllSelected,
    handleUpdateRowsPerPage,
  };
};

export { useMediaTable, actionTypes };
